/**
 * DriverRoutePanel — shows the driver's active-order stops in the optimal
 * nearest-neighbor sequence (backend `GET /api/driver/route`). Auto-refreshes
 * every 15s. Renders as a numbered list; if 2+ stops, also embeds a compact
 * SVG polyline for a quick visual overview.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Route as RouteIcon, MapPin, Store, Home, RefreshCw, Loader2, Navigation2 } from 'lucide-react';
import { driverApi } from '../services/api';
import { Button } from '../components/ui/button';
import NavigateDialog from '../components/NavigateDialog';

const DriverRoutePanel = () => {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navStop, setNavStop] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await driverApi.route();
      setRoute(r);
    } catch (_e) {
      setRoute(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  if (loading && !route) {
    return <section className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Building your optimal route…</section>;
  }
  if (!route || !route.stops || route.stops.length === 0) return null;

  const stops = route.stops;
  const lats = stops.map((s) => s.lat).filter((v) => v != null);
  const lngs = stops.map((s) => s.lng).filter((v) => v != null);
  if (lats.length && route.driver_lat != null) { lats.push(route.driver_lat); lngs.push(route.driver_lng); }
  const mnla = Math.min(...lats); const mxla = Math.max(...lats);
  const mnlo = Math.min(...lngs); const mxlo = Math.max(...lngs);
  const span = (v, mn, mx) => (mx === mn ? 0.5 : (v - mn) / (mx - mn));
  const W = 400; const H = 140; const pad = 12;
  const px = (lng) => pad + span(lng, mnlo, mxlo) * (W - 2 * pad);
  const py = (lat) => H - (pad + span(lat, mnla, mxla) * (H - 2 * pad));

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="driver-route-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-900">
            <RouteIcon className="h-5 w-5 text-emerald-600" /> Your optimal route
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {stops.length} stops · {route.total_km} km total. Same-vendor pickups are grouped into a single stop.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="route-reload-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {stops.length >= 2 && (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32 mt-3 rounded-lg bg-slate-50 border border-slate-100">
          {route.driver_lat != null && route.driver_lng != null && (
            <>
              <line
                x1={px(route.driver_lng)} y1={py(route.driver_lat)}
                x2={px(stops[0].lng)}      y2={py(stops[0].lat)}
                stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="4 3"
              />
              <circle cx={px(route.driver_lng)} cy={py(route.driver_lat)} r="4" fill="#0F172A" />
            </>
          )}
          <polyline
            points={stops.map((s) => `${px(s.lng)},${py(s.lat)}`).join(' ')}
            fill="none" stroke="#059669" strokeWidth="2"
          />
          {stops.map((s, i) => (
            <g key={i}>
              <circle cx={px(s.lng)} cy={py(s.lat)} r="7" fill={s.type === 'pickup' ? '#0EA5E9' : '#10B981'} />
              <text x={px(s.lng)} y={py(s.lat) + 3} fontSize="9" fill="#fff" fontWeight="700" textAnchor="middle">{s.sequence}</text>
            </g>
          ))}
        </svg>
      )}

      <ol className="mt-3 space-y-1.5" data-testid="route-stops-list">
        {stops.map((s) => (
          <li key={s.sequence} className="flex items-center gap-2.5 text-sm" data-testid={`route-stop-${s.sequence}`}>
            <span className={`h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${s.type === 'pickup' ? 'bg-sky-500' : 'bg-emerald-600'}`}>{s.sequence}</span>
            {s.type === 'pickup' ? <Store className="h-4 w-4 text-sky-600" /> : <Home className="h-4 w-4 text-emerald-600" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate">
                {s.type === 'pickup' ? `Pickup · ${s.vendor_name || 'Vendor'}` : 'Drop-off'}
              </div>
              <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                <MapPin className="h-3 w-3" />
                {s.address || `${s.lat.toFixed(4)}, ${s.lng.toFixed(4)}`}
                {s.distance_km_from_prev != null && <span className="text-slate-400">· {s.distance_km_from_prev} km</span>}
                {s.order_ids && s.order_ids.length > 1 && (
                  <span className="text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-1.5 py-0.5">
                    {s.order_ids.length} orders batched
                  </span>
                )}
              </div>
            </div>
            {s.lat != null && s.lng != null && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2.5 shrink-0"
                onClick={() => setNavStop(s)}
                data-testid={`navigate-stop-${s.sequence}`}
                title="Open in-app navigation for this stop"
              >
                <Navigation2 className="h-3.5 w-3.5 mr-1 text-emerald-600" />
                <span className="hidden sm:inline">Navigate</span>
              </Button>
            )}
          </li>
        ))}
      </ol>

      <NavigateDialog
        open={Boolean(navStop)}
        onClose={() => setNavStop(null)}
        origin={route.driver_lat != null && route.driver_lng != null ? { lat: route.driver_lat, lng: route.driver_lng } : null}
        destination={navStop ? {
          lat: navStop.lat,
          lng: navStop.lng,
          address: navStop.address,
          label: navStop.type === 'pickup' ? `Pickup · ${navStop.vendor_name || 'Vendor'}` : `Drop-off #${navStop.sequence}`,
        } : null}
      />
    </section>
  );
};

export default DriverRoutePanel;
